import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { CurrencySwitcherModal } from "@airhelp/react";
import { useDisclosure } from "@chakra-ui/react";
import { setAhplusCurrency, useAhplusSettingsSnapshot } from "hooks/useAhPlusSettings";
import {
  ahPlusCurrencies,
  AhpCurrency,
  AhpCurrencySettings,
} from "app-settings/localesAndCurrencies";
import { analyticsGA4 } from "tracking/analytics";
import { cta_click } from "tracking/ga4/events/other";
import { CountryCode } from "@airhelp/flags";

interface CurrencyListItem {
  id: string;
  name: string;
  icon: CountryCode;
  code: string;
  symbol: string;
}

interface Props {
  render: (openModalHandler: () => void) => void;
  onCurrencyChange?: (currency: AhpCurrency) => void;
}

const AhPlusCurrencySwitcher: FC<Props> = ({ render, onCurrencyChange }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currency } = useAhplusSettingsSnapshot();

  const getCurrencyListItems = (): CurrencyListItem[] => {
    const currencies: AhpCurrencySettings[] = Object.values(ahPlusCurrencies);

    return currencies.map((currencyData: AhpCurrencySettings): CurrencyListItem => {
      return {
        id: currencyData.id,
        name: t(currencyData.name),
        icon: currencyData.icon,
        code: currencyData.code,
        symbol: currencyData.symbol,
      };
    });
  };

  const currencyListItems = getCurrencyListItems();
  const currencyList = { listItems: currencyListItems };

  const onItemClick = async (data: CurrencyListItem): Promise<void> => {
    const newCurrency = data.code as AhpCurrency;
    analyticsGA4.track("cta_click", cta_click(`${newCurrency}`, "claim successful AH+"));

    setAhplusCurrency(newCurrency);
    onCurrencyChange?.(newCurrency);

    onClose();
  };

  const onOpenCallback = () => {
    analyticsGA4.track("cta_click", cta_click("currency selector", "claim successful AH+"));
    onOpen();
  };

  return (
    <CurrencySwitcherModal
      heading={t("footer.settings_modal.select_currency")}
      selectedCurrency={currency}
      currencies={currencyList}
      onItemClick={onItemClick}
      onClose={onClose}
      onOpen={onOpenCallback}
      isOpen={isOpen}
      render={render}
    />
  );
};

export default AhPlusCurrencySwitcher;
